import { Select } from '@/components/ui/select';
import { LuPencil } from 'react-icons/lu';

import { useVersions } from '@/hooks/useVersion';
import { useVersionsWithSpecialties } from '@/hooks/useVersionsWithSpecialties';
import { useResetQuestionnaire } from '@/hooks/useResetQuestionnaire';
import { cn } from '@/lib/utils';
import { getQuestionnaireVariants } from '@/util/questionnaire';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '@/types/graph';
import { selectQueryVariant } from '@/selectors/version';
import { setQuestionnaireConfig } from '@/reducers/graph';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

export const Version = () => {
  const reset = useResetQuestionnaire();
  const versions = useVersions();
  const questionnaireVariants = getQuestionnaireVariants();
  const { data: versionsWithSpecialties, loading: versionsLoading } =
    useVersionsWithSpecialties();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [optionsJson, setOptionsJson] = useState('');
  const [pagesJson, setPagesJson] = useState('');

  const currentVariant = useSelector(
    (state: RootState) => state.questionnaire.config?.slug,
  );
  const queryVariant = useSelector(selectQueryVariant);
  const prevousQueryVariant = useSelector(
    (state: RootState) => state.previousQueryVariant,
  );
  const professionalVariant = useSelector(
    (state: RootState) =>
      state.consultation.professional?.defaultQuestionnaireVariant ?? undefined,
  );
  const consultationVariant = useSelector(
    (state: RootState) => state.consultation.variant,
  );
  const questionnaireConfig = useSelector(
    (state: RootState) => state.questionnaire.config,
  );

  const getVariantSourceIndicators = (variantSlug: string) => {
    const indicators = [];

    if (currentVariant === variantSlug) {
      indicators.push('(current)');
    }

    if (prevousQueryVariant === variantSlug) {
      indicators.push('(previous query)');
    }

    if (queryVariant === variantSlug) {
      indicators.push('(query)');
    }

    if (professionalVariant === variantSlug) {
      indicators.push('(pro default)');
    }

    if (consultationVariant === variantSlug) {
      indicators.push('(consultation)');
    }

    return indicators;
  };

  const [currentVersionSpecialties, setCurrentVersionSpecialties] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (versions.current && versionsWithSpecialties) {
      versionsWithSpecialties.versionsWithSpecialties.forEach((v) => {
        if (v.version === versions.current) {
          setCurrentVersionSpecialties(v.specialties);
          return;
        }
      });
    }
  }, [setCurrentVersionSpecialties, versionsWithSpecialties, versions.current]);

  const handleEditClick = () => {
    if (questionnaireConfig) {
      setIsEditing(true);
      setOptionsJson(
        JSON.stringify(questionnaireConfig.options || [], null, 2),
      );
      setPagesJson(JSON.stringify(questionnaireConfig.pages || {}, null, 2));
      dispatch(setQuestionnaireConfig(questionnaireConfig));
    }
  };

  const handleSave = () => {
    if (!questionnaireConfig) return;

    let parsedOptions;
    let parsedPages;

    try {
      parsedOptions = JSON.parse(optionsJson);
    } catch {
      enqueueSnackbar('Invalid Options JSON', { variant: 'error' });
      return;
    }

    try {
      parsedPages = JSON.parse(pagesJson);
    } catch {
      enqueueSnackbar('Invalid Pages JSON', { variant: 'error' });
      return;
    }

    dispatch(
      setQuestionnaireConfig({
        ...questionnaireConfig,
        options: parsedOptions,
        pages: parsedPages,
      }),
    );
    setIsEditing(false);
  };

  return (
    <div className="flex flex-col gap-6 p-4 bg-[var(--dbg-bg)] text-[var(--dbg-text)]">
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-medium text-[var(--dbg-text)]">Version</h3>
        {!versionsLoading && (
          <Select
            value={versions?.current}
            onChange={(e) =>
              reset({
                version: e.target.value.toString(),
                clearStorage: true,
              })
            }
            className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] border-[var(--dbg-border)] hover:border-[var(--dbg-border-input)] focus:ring-[var(--dbg-accent)]"
          >
            {versionsWithSpecialties?.versionsWithSpecialties.map((v) => (
              <option key={v.version} value={v.version}>
                {v.version}
                {v.specialties.map((s) => ` ${s}`).join('')}
                {versions.current && v.version === versions.current
                  ? ' (current)'
                  : ''}
                {versions.api && v.version === versions.api ? ' (api)' : ''}
                {versions.env && v.version === versions.env ? ' (env)' : ''}
                {versions.query && v.version === versions.query
                  ? ' (query)'
                  : ''}
              </option>
            ))}
          </Select>
        )}
      </div>

      <div
        className={cn(
          'flex flex-col gap-2',
          !questionnaireVariants.length && 'opacity-30',
        )}
      >
        <h3 className="text-lg font-medium text-[var(--dbg-text)]">
          Questionnaire Variant
        </h3>
        {!versionsLoading && (
          <Select
            value={currentVariant || ''}
            onChange={(e) =>
              reset({
                variant: e.target.value,
                version: versions.current?.toString(),
                clearStorage: true,
              })
            }
            disabled={!questionnaireVariants.length}
            className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] border-[var(--dbg-border)] hover:border-[var(--dbg-border-input)] focus:ring-[var(--dbg-accent)]"
          >
            {questionnaireVariants.map((variant) => {
              const sourceIndicators = getVariantSourceIndicators(variant.slug);
              return (
                <option
                  key={variant.slug}
                  value={variant.slug}
                  disabled={
                    !currentVersionSpecialties.includes(variant.speciality)
                  }
                >
                  {variant.name} ({variant.slug})
                  {sourceIndicators.length > 0
                    ? ` ${sourceIndicators.join(' ')}`
                    : ''}
                </option>
              );
            })}
          </Select>
        )}
      </div>

      {questionnaireConfig && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-[var(--dbg-text)]">
              Questionnaire Config
            </h3>
            <button
              onClick={handleEditClick}
              className="p-1 text-[var(--dbg-text-secondary)] hover:text-[var(--dbg-text)] cursor-pointer"
            >
              <LuPencil className="size-4" />
            </button>
          </div>
          <div className="bg-[var(--dbg-bg-secondary)] p-4 rounded border border-[var(--dbg-border)]">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-[var(--dbg-text-secondary)]">Slug:</span>{' '}
                <span className="text-[var(--dbg-text)]">
                  {questionnaireConfig.slug}
                </span>
              </div>
              <div>
                <span className="text-[var(--dbg-text-secondary)]">Name:</span>{' '}
                <span className="text-[var(--dbg-text)]">
                  {questionnaireConfig.name}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-[var(--dbg-text-secondary)]">
                  Description:
                </span>{' '}
                <span className="text-[var(--dbg-text)]">
                  {questionnaireConfig.description}
                </span>
              </div>
              <div>
                <span className="text-[var(--dbg-text-secondary)]">
                  Speciality:
                </span>{' '}
                <span className="text-[var(--dbg-text)]">
                  {questionnaireConfig.speciality}
                </span>
              </div>
              <div>
                <span className="text-[var(--dbg-text-secondary)]">
                  Patient Onboarding:
                </span>{' '}
                <span className="text-[var(--dbg-text)]">
                  {questionnaireConfig.patientOnboarding}
                </span>
              </div>
              <div>
                <span className="text-[var(--dbg-text-secondary)]">
                  Patient Offboarding:
                </span>{' '}
                <span className="text-[var(--dbg-text)]">
                  {questionnaireConfig.patientOffboarding}
                </span>
              </div>
              <div>
                <span className="text-[var(--dbg-text-secondary)]">
                  Email Template:
                </span>{' '}
                <span className="text-[var(--dbg-text)]">
                  {questionnaireConfig.emailTemplate}
                </span>
              </div>
              <div>
                <span className="text-[var(--dbg-text-secondary)]">
                  Report Template:
                </span>{' '}
                <span className="text-[var(--dbg-text)]">
                  {questionnaireConfig.reportTemplate}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-[var(--dbg-text-secondary)]">
                  Options:
                </span>
                {isEditing ? (
                  <textarea
                    rows={4}
                    value={optionsJson}
                    onChange={(e) => setOptionsJson(e.target.value)}
                    className="w-full mt-1 bg-[var(--dbg-bg-code)] text-[var(--dbg-text)] border border-[var(--dbg-border)] rounded px-3 py-2 text-sm font-mono focus:outline-none resize-none"
                  />
                ) : (
                  <span className="text-[var(--dbg-text)]">
                    {' '}
                    {questionnaireConfig.options?.join(', ') || 'None'}
                  </span>
                )}
              </div>
            </div>
            <div className="mt-4 text-sm">
              <span className="text-[var(--dbg-text-secondary)]">Pages:</span>
              {isEditing ? (
                <textarea
                  rows={16}
                  value={pagesJson}
                  onChange={(e) => setPagesJson(e.target.value)}
                  className="w-full mt-1 bg-[var(--dbg-bg-code)] text-[var(--dbg-text)] border border-[var(--dbg-border)] rounded px-3 py-2 text-sm font-mono focus:outline-none resize-none"
                />
              ) : (
                <div className="mt-2 max-h-full flex flex-col gap-2">
                  {Object.entries(questionnaireConfig.pages).map(
                    ([key, page]) => (
                      <div
                        key={key}
                        className="bg-[var(--dbg-bg)] p-2 rounded border-l-4 border-[var(--dbg-accent)]"
                      >
                        <div className="text-[var(--dbg-text)]">
                          <span className="font-medium">{key}</span> -{' '}
                          {page.title}
                        </div>
                        <div className="text-xs text-[var(--dbg-text-secondary)] mt-1">
                          {page.sections.join(', ')}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
            {isEditing && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer text-sm"
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
